import { useState, useCallback } from 'react';
import { createScaledCanvasPreview } from '../utils/canvasPreview';
import type { CanvasPreviewOptions } from '../utils/canvasPreview';

/**
 * Custom hook for canvas preview with React memoization
 * Uses React.memo patterns to prevent unnecessary re-renders and processing
 */
export function useCanvasPreview() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePreview = useCallback(async (options: CanvasPreviewOptions): Promise<string> => {
    setIsGenerating(true);
    setError(null);

    try {
      const result = await createScaledCanvasPreview(options);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate preview';
      setError(errorMessage);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  // Memoize the preview generation based on options
  const memoizedPreview = useCallback(
    (options: CanvasPreviewOptions) => {
      return generatePreview(options);
    },
    [generatePreview]
  );

  return {
    generatePreview: memoizedPreview,
    isGenerating,
    error
  };
}
